import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/shared/api/useApi';
import type { PluginCatalogueItem, PluginGrantRequest } from '@/shared/api/types/plugins';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@/shared/components';
import { PluginProblem } from './PluginProblem';
import { pluginCapabilityLabel } from './pluginPresentation';
import { usePluginDialogFocus } from './usePluginDialogFocus';

export function PluginSetupDialog({
  plugin,
  onClose,
}: {
  plugin: PluginCatalogueItem;
  onClose: () => void;
}) {
  const api = useApi();
  const returnFocus = usePluginDialogFocus();
  const cache = useQueryClient();
  const [consent, setConsent] = useState(false);
  const [pii, setPii] = useState(false);
  const [discard, setDiscard] = useState(false);
  const resume = plugin.installation?.state === 'Disabled';
  const mutation = useMutation({
    networkMode: 'always',
    mutationFn: () => {
      const grant: PluginGrantRequest = {
        scope: 'TenantAll',
        capabilities: plugin.capabilities,
        guestCommunicationPii: pii,
      };
      return resume && plugin.installation
        ? api.plugins.resume(plugin.installation.id, plugin.installation.grantRevision, grant)
        : api.plugins.install(plugin.id, grant);
    },
    onSuccess: onClose,
    onSettled: () => cache.invalidateQueries({ queryKey: ['plugins'] }),
  });
  const close = () => {
    if (mutation.isPending) return;
    if (consent || pii) setDiscard(true);
    else onClose();
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent onCloseAutoFocus={returnFocus}>
        <DialogHeader>
          <DialogTitle>
            {plugin.name} {resume ? 'erneut aktivieren' : 'einrichten'}
          </DialogTitle>
          <DialogDescription>
            Sie erlauben diesem Plugin die unten aufgeführten Datenzugriffe und Aktionen für die
            freigegebenen Apartments Ihres Workspaces.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-5">
          <ul className="text-ink-soft list-disc space-y-2 pl-5 text-sm">
            {plugin.capabilities.map((cap) => (
              <li key={cap}>{pluginCapabilityLabel(cap)}</li>
            ))}
          </ul>
          <label className="border-line flex min-h-12 cursor-pointer items-start gap-3 rounded-md border p-4 text-sm">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="accent-sage mt-1 h-5 w-5 shrink-0"
            />
            Ich gebe alle aktuellen und künftigen Apartments dieses Workspaces frei.
          </label>
          {plugin.canGrantPii && (
            <label className="flex min-h-12 cursor-pointer items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={pii}
                onChange={(e) => setPii(e.target.checked)}
                className="accent-sage mt-1 h-5 w-5 shrink-0"
              />
              Gästekommunikation mit personenbezogenen Inhalten freigeben.
            </label>
          )}
          <p className="text-ink-soft text-sm">
            Jedes Teammitglied benötigt anschließend eine eigene Freigabe unter „Team & Rechte“ und
            verbindet seine eigene Identität in GuestAI.
          </p>
          <PluginProblem error={mutation.error} />
          {discard && (
            <div className="border-amber rounded-md border p-4" role="alert">
              <p>Ihre Auswahl verwerfen?</p>
              <Button type="button" size="lg" onClick={() => setDiscard(false)}>
                Weiter bearbeiten
              </Button>
              <Button type="button" size="lg" onClick={onClose}>
                Auswahl verwerfen
              </Button>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" size="lg" disabled={mutation.isPending} onClick={close}>
            Abbrechen
          </Button>
          <Button
            type="button"
            size="lg"
            variant="brand"
            loading={mutation.isPending}
            disabled={!consent || !plugin.entitled || !plugin.canManage}
            onClick={() => mutation.mutate()}
          >
            {resume ? 'Erneut aktivieren' : 'Plugin aktivieren'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
